import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'

import ScreenLayout from '@/components/layout/ScreenLayout'
import DataTable, { type DataTableColumn } from '@/components/ui/DataTable'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import EmptyState from '@/components/ui/EmptyState'
import Money from '@/components/ui/Money'
import RefreshRegion from '@/components/ui/RefreshRegion'
import TabBar, { TabPanel, type TabBarOption } from '@/components/ui/TabBar'
import TermTip from '@/components/ui/TermTip'
import TitleRow from '@/components/ui/TitleRow'
import { useIndexSymbols, useMomentumMovers, useSteadyStocks } from '@/features/signals/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { formatMoney } from '@/lib/format'
import type { Money as MoneyValue } from '@/lib/money'
import { lastUpdatedMeta, resolveViewState } from '@/lib/view-state'
import type { MomentumRow, SteadyRow } from '@/schemas/signals'
import {
  ESSENTIALS_TABS,
  essentialsSearchSchema,
  loadEssentials,
  momentumQueryOf,
  steadyQueryOf,
  type EssentialsTab,
} from '@/screens/signals/EssentialsScreen.loader'
import MissingFigure from '@/screens/signals/components/MissingFigure'
import PastSessionEmpty from '@/screens/signals/components/PastSessionEmpty'
import SessionDatePicker from '@/screens/signals/components/SessionDatePicker'
import SignalsEmpty from '@/screens/signals/components/SignalsEmpty'
import SignalsLocked from '@/screens/signals/components/SignalsLocked'
import SignalsRouteError from '@/screens/signals/components/SignalsRouteError'
import { signalCompanyColumn } from '@/screens/signals/components/SignalCompanyColumn'
import { PickerSkeleton } from '@/screens/signals/components/SignalsSkeletons'
import SignalsTableFrame from '@/screens/signals/components/SignalsTableFrame'
import {
  TableFrameSkeleton,
  TabsSkeleton,
  ToolbarSkeleton,
} from '@/screens/signals/components/SignalsTableSkeletons'
import SignalsToolbar from '@/screens/signals/components/SignalsToolbar'
import { signalsRailProps } from '@/screens/signals/components/signals-rail'
import { onSession, useIsPastSession, useSessionStatus } from '@/screens/signals/lib/session-status'
import { useShownValue } from '@/screens/signals/lib/shown-value'
import { isSignalsLocked, signalRowLink } from '@/screens/signals/lib/signals-ui'
import { SIGNAL_DEFAULT_PAGE_SIZE, toSignalPageSize } from '@/screens/signals/search'

const TITLE = 'Essentials'

/** Momentum is short by rule (3 rows on dev), so three rows stand in for it. */
const MOMENTUM_SKELETON_ROWS = 3

const TAB_OPTIONS: readonly TabBarOption[] = [
  { value: 'steady', label: 'Steady Stocks' },
  { value: 'momentum', label: 'Momentum Movers' },
]

const isTab = (value: string): value is EssentialsTab =>
  ESSENTIALS_TABS.some((tab) => tab === value)

const TABS_ID = 'essentials'

/*
 * The loaded page, block for block: the tab row with its date control at the
 * right (the session picker on both) and the open list
 * (title row, table, pager). The tab comes off the URL, as Floorsheet's
 * pending page reads its view.
 */
function EssentialsPending() {
  const { tab } = Route.useSearch()
  const steady = tab === 'steady'
  return (
    <ScreenLayout
      pending
      pendingLabel="Loading essentials…"
      header={<TitleRow title={TITLE} />}
      {...signalsRailProps(TITLE)}
    >
      <div aria-hidden="true">
        <div className="mb-6">
          <ToolbarSkeleton start={<TabsSkeleton className="w-60" />} end={<PickerSkeleton />} />
        </div>
        {steady ? (
          <TableFrameSkeleton rows={SIGNAL_DEFAULT_PAGE_SIZE} columns={4} title />
        ) : (
          <TableFrameSkeleton rows={MOMENTUM_SKELETON_ROWS} columns={3} title pager={false} />
        )}
      </div>
    </ScreenLayout>
  )
}

function EssentialsError(props: ErrorComponentProps) {
  return (
    <SignalsRouteError
      {...props}
      header={<TitleRow title={TITLE} />}
      pageTitle={TITLE}
      title="Essentials did not load"
    />
  )
}

export const Route = createFileRoute('/_app/market/signals/essentials')({
  validateSearch: essentialsSearchSchema,
  loader: loadEssentials,
  pendingComponent: EssentialsPending,
  errorComponent: EssentialsError,
  component: EssentialsScreen,
})

/**
 * The lead Company column takes what the figures leave (mock `szt` Company flex), the figures
 * holding the mock's 84 / 90 widths (both 88 on the grid) at the right edge.
 */
const companyColumn = <Row extends SteadyRow | MomentumRow>(): DataTableColumn<Row> => ({
  ...signalCompanyColumn<Row>(),
  meta: { fill: { min: 128 } },
})

const FIGURE_WIDTH = '5.5rem'

/**
 * "LTP (Rs)" as the mock's dotted head term with its band card (A3), the figure 12/400 white
 * beneath it. `ltp` is the session's close, so the figure is the last traded price.
 */
const ltpColumn = <Row extends { ltp: MoneyValue | null }>(): DataTableColumn<Row> => ({
  id: 'close',
  accessorFn: (row) => row.ltp ?? '',
  header: () => (
    <TermTip term="price" variant="band">
      LTP (Rs)
    </TermTip>
  ),
  enableSorting: false,
  meta: { numeric: true, width: FIGURE_WIDTH },
  cell: ({ row }) =>
    row.original.ltp === null ? <MissingFigure /> : <Money value={row.original.ltp} />,
})

/** Module-level, so the cells keep their identity. */
const STEADY_COLUMNS: DataTableColumn<SteadyRow>[] = [
  companyColumn<SteadyRow>(),
  {
    id: 'low',
    accessorFn: (row) => row.low,
    header: 'Range low',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: FIGURE_WIDTH },
    cell: ({ row }) => <Money value={row.original.low} />,
  },
  {
    id: 'high',
    accessorFn: (row) => row.high,
    header: 'Range high',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: FIGURE_WIDTH },
    cell: ({ row }) => <Money value={row.original.high} />,
  },
  ltpColumn<SteadyRow>(),
]

/** Highest RSI first, as the API sends it (RSI itself is not drawn). `ltp` and volatility can be null. */
const MOMENTUM_COLUMNS: DataTableColumn<MomentumRow>[] = [
  companyColumn<MomentumRow>(),
  {
    id: 'volatility',
    accessorFn: (row) => row.volatilityPercent ?? '',
    header: 'Volatility',
    enableSorting: false,
    meta: { numeric: true, secondary: true, width: FIGURE_WIDTH },
    cell: ({ row }) =>
      row.original.volatilityPercent === null ? (
        <MissingFigure />
      ) : (
        <span className="tnum">{formatMoney(row.original.volatilityPercent)}%</span>
      ),
  },
  ltpColumn<MomentumRow>(),
]

/**
 * Signal Zone · Essentials — two rule-based lists, one at a time behind
 * underline tabs (`?tab=`): stocks that held a tight band on a session
 * (Steady, tightest first) and stocks with the strongest recent momentum
 * (highest RSI first). Both in the API's order, each in its own card.
 *
 * The session picker drives Steady only: Momentum takes no date, so it serves the latest
 * session and a past pick shows `PastSessionEmpty` there (`asks/signals-dates.md`). A Momentum failure
 * (B7: a 500 when mm-be's technical job is absent) is that card's own, never
 * the page's. Steady is the MAIN read, keyed on date and page: `useQuery` +
 * `keepPreviousData`, the old rows held under RefreshRegion's refresh line
 * until the new ones land. Only the open tab's list is drawn, but both reads
 * are warm, so a switch is instant. Momentum pages on its own params, so every
 * name its count promises is reachable.
 */
function EssentialsScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const online = useOnline()
  const steady = useSteadyStocks(steadyQueryOf(search))
  const momentum = useMomentumMovers(momentumQueryOf(search))
  const steadyView = resolveViewState(steady, { online })
  const momentumView = resolveViewState(momentum, { online })
  const page = steady.data
  const isUpdating = steady.isPlaceholderData
  const indexSymbols = useIndexSymbols()
  // The session the rows on screen are for: the answer's own, else the one it was asked for.
  const shownDate = useShownValue(search.date ?? null, isUpdating)
  const session = page?.date ?? shownDate
  const sessionStatus = useSessionStatus(session)

  const momentumPast = useIsPastSession(search.date)

  const onSessionChange = (date: string) => {
    void navigate({ search: (prev) => ({ ...prev, date, page: 1 }) })
  }

  const steadyBody = (() => {
    if (steadyView === 'failed' || steadyView === 'offline') {
      return isSignalsLocked(steady.error) ? (
        <SignalsLocked error={steady.error} />
      ) : (
        <EmptyState
          kind={steadyView}
          size="section"
          onRetry={() => void steady.refetch()}
          retrying={steady.isFetching}
          meta={steadyView === 'failed' ? lastUpdatedMeta(steady.dataUpdatedAt) : undefined}
          {...(steadyView === 'failed' ? { title: 'Couldn’t load steady stocks' } : {})}
        />
      )
    }
    if (page === undefined)
      return <DataTableSkeleton rows={SIGNAL_DEFAULT_PAGE_SIZE} columns={4} rowHeight="lg" />
    if (page.rows.length === 0) {
      return (
        <RefreshRegion updating={isUpdating}>
          {page.total > 0 ? (
            <SignalsEmpty
              reason="past-end"
              of="the list"
              size="section"
              onFirstPage={() => {
                void navigate({ search: (prev) => ({ ...prev, page: 1 }) })
              }}
            />
          ) : sessionStatus.computed === false ? (
            <SignalsEmpty reason="session" date={sessionStatus.date} size="section" />
          ) : (
            <SignalsEmpty
              reason="list"
              size="section"
              title={`No steady stocks ${onSession(sessionStatus.date)}`}
              description="No stock held a tight enough band on that session."
            />
          )}
        </RefreshRegion>
      )
    }
    return (
      <RefreshRegion updating={isUpdating}>
        <DataTable
          rowHeight="lg"
          columns={STEADY_COLUMNS}
          rows={page.rows}
          getRowId={(row) => row.stock.symbol}
          rowLinkOptions={signalRowLink(indexSymbols)}
          washTone="wash"
          nameUnderline={false}
          headWeight="regular"
          caption="Steady stocks, tightest band first"
        />
      </RefreshRegion>
    )
  })()

  const movers = momentum.data
  const momentumBody = (() => {
    if (momentumPast && search.date !== undefined) {
      return <PastSessionEmpty date={search.date} size="section" />
    }
    if (momentumView === 'failed' || momentumView === 'offline') {
      return isSignalsLocked(momentum.error) ? (
        <SignalsLocked error={momentum.error} size="section" />
      ) : (
        <EmptyState
          kind={momentumView}
          size="section"
          onRetry={() => void momentum.refetch()}
          retrying={momentum.isFetching}
          meta={momentumView === 'failed' ? lastUpdatedMeta(momentum.dataUpdatedAt) : undefined}
          {...(momentumView === 'failed' ? { title: 'Couldn’t load momentum movers' } : {})}
        />
      )
    }
    if (movers === undefined)
      return <DataTableSkeleton rows={MOMENTUM_SKELETON_ROWS} columns={3} rowHeight="lg" />
    if (movers.rows.length === 0) {
      return (
        <RefreshRegion updating={momentum.isPlaceholderData}>
          {movers.total > 0 ? (
            <SignalsEmpty
              reason="past-end"
              of="the list"
              size="section"
              onFirstPage={() => {
                void navigate({ search: (prev) => ({ ...prev, momentumPage: 1 }) })
              }}
            />
          ) : (
            <SignalsEmpty
              reason="list"
              size="section"
              title="No momentum movers right now"
              description="The rule is strict, so a quiet market can leave this list empty."
            />
          )}
        </RefreshRegion>
      )
    }
    return (
      <RefreshRegion updating={momentum.isPlaceholderData}>
        <DataTable
          rowHeight="lg"
          columns={MOMENTUM_COLUMNS}
          rows={movers.rows}
          getRowId={(row) => row.stock.symbol}
          rowLinkOptions={signalRowLink(indexSymbols)}
          washTone="wash"
          nameUnderline={false}
          headWeight="regular"
          caption="Momentum movers, highest RSI first"
        />
      </RefreshRegion>
    )
  })()

  const steadyShown = page !== undefined && page.rows.length > 0
  const momentumShown = !momentumPast && movers !== undefined && movers.rows.length > 0

  return (
    <ScreenLayout header={<TitleRow title={TITLE} />} {...signalsRailProps(TITLE)}>
      <div>
        <SignalsToolbar
          className="mb-6"
          start={
            <TabBar
              ariaLabel="Essentials lists"
              idBase={TABS_ID}
              options={TAB_OPTIONS}
              value={search.tab}
              onChange={(next) => {
                if (isTab(next)) void navigate({ search: (prev) => ({ ...prev, tab: next }) })
              }}
            />
          }
          end={
            <SessionDatePicker
              value={search.date ?? (search.tab === 'steady' ? page?.date : undefined) ?? null}
              onChange={onSessionChange}
            />
          }
        />

        <TabPanel idBase={TABS_ID} value={search.tab}>
          {search.tab === 'steady' ? (
            <SignalsTableFrame
              title="Steady stocks"
              {...(page === undefined || page.total === 0 ? {} : { count: page.total })}
              {...(steadyShown
                ? {
                    paging: {
                      page: page.page,
                      pageSize: page.pageSize,
                      total: page.total,
                      onPageChange: (next: number) => {
                        void navigate({ search: (prev) => ({ ...prev, page: next }) })
                      },
                      onPageSizeChange: (pageSize) => {
                        void navigate({
                          search: (prev) => ({
                            ...prev,
                            pageSize: toSignalPageSize(pageSize, prev.pageSize),
                            page: 1,
                          }),
                        })
                      },
                    },
                  }
                : {})}
            >
              {steadyBody}
            </SignalsTableFrame>
          ) : (
            <SignalsTableFrame
              title="Momentum movers"
              {...(momentumPast || movers === undefined || movers.total === 0
                ? {}
                : { count: movers.total })}
              {...(momentumShown
                ? {
                    paging: {
                      page: movers.page,
                      pageSize: movers.pageSize,
                      total: movers.total,
                      onPageChange: (next: number) => {
                        void navigate({ search: (prev) => ({ ...prev, momentumPage: next }) })
                      },
                      onPageSizeChange: (pageSize) => {
                        void navigate({
                          search: (prev) => ({
                            ...prev,
                            momentumPageSize: toSignalPageSize(pageSize, prev.momentumPageSize),
                            momentumPage: 1,
                          }),
                        })
                      },
                    },
                  }
                : {})}
            >
              {momentumBody}
            </SignalsTableFrame>
          )}
        </TabPanel>
      </div>
    </ScreenLayout>
  )
}
